import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Image, Trash2, Upload, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ImageGalleryModal({ open, onClose, onSelect }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageSize, setImageSize] = useState('medium');
  const [imageAlign, setImageAlign] = useState('center');

  useEffect(() => {
    if (open) {
      fetchImages();
    }
  }, [open]);

  const fetchImages = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/gallery`, { withCredentials: true });
      setImages(response.data);
    } catch (error) {
      toast.error('Failed to load images');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API}/upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Image uploaded');
      fetchImages();
      setSelectedImage(response.data.url);
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (filename, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this image?')) return;

    try {
      await axios.delete(`${API}/gallery/${filename}`, { withCredentials: true });
      toast.success('Image deleted');
      fetchImages();
      if (selectedImage?.includes(filename)) {
        setSelectedImage(null);
      }
    } catch (error) {
      toast.error('Failed to delete image');
    }
  };

  const handleInsert = () => {
    if (!selectedImage) return;

    const sizeMap = {
      small: '25%',
      medium: '50%',
      large: '75%',
      full: '100%'
    };

    const fullUrl = selectedImage.startsWith('/api')
      ? `${process.env.REACT_APP_BACKEND_URL}${selectedImage}`
      : selectedImage;

    onSelect({
      url: fullUrl,
      size: sizeMap[imageSize],
      align: imageAlign
    });
    
    onClose();
    setSelectedImage(null);
    setImageSize('medium');
    setImageAlign('center');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Image Gallery</DialogTitle>
          <DialogDescription>
            Select an image from the gallery or upload a new one
          </DialogDescription>
        </DialogHeader>

        {/* Upload Button */}
        <div className="flex items-center gap-4 py-2">
          <input
            type="file"
            id="gallery-upload"
            onChange={handleUpload}
            accept="image/*"
            className="hidden"
          />
          <label htmlFor="gallery-upload">
            <Button asChild variant="outline" disabled={uploading}>
              <span>
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? 'Uploading...' : 'Upload New Image'}
              </span>
            </Button>
          </label>
        </div>

        {/* Image Grid */}
        <div className="flex-1 overflow-y-auto border rounded-lg p-4 min-h-[300px]">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : images.length > 0 ? (
            <div className="grid grid-cols-4 gap-4">
              {images.map((img) => (
                <div
                  key={img.filename}
                  onClick={() => setSelectedImage(img.url)}
                  className={`relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 transition-all hover:opacity-90 ${
                    selectedImage === img.url
                      ? 'border-primary ring-2 ring-primary'
                      : 'border-transparent'
                  }`}
                >
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${img.url}`}
                    alt={img.filename}
                    className="w-full h-full object-cover"
                  />
                  {selectedImage === img.url && (
                    <div className="absolute top-2 right-2 bg-primary text-primary-foreground rounded-full p-1">
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                  <button
                    onClick={(e) => handleDelete(img.filename, e)}
                    className="absolute bottom-2 right-2 bg-destructive text-destructive-foreground rounded-full p-1 opacity-0 hover:opacity-100 transition-opacity"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                  <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-1 truncate">
                    {formatFileSize(img.size)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <Image className="h-12 w-12 mb-4 opacity-50" />
              <p>No images yet. Upload one to get started!</p>
            </div>
          )}
        </div>

        {/* Size and Alignment Options */}
        {selectedImage && (
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div className="space-y-2">
              <Label>Size</Label>
              <Select value={imageSize} onValueChange={setImageSize}>
                <SelectTrigger>
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">Small (25%)</SelectItem>
                  <SelectItem value="medium">Medium (50%)</SelectItem>
                  <SelectItem value="large">Large (75%)</SelectItem>
                  <SelectItem value="full">Full Width (100%)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Alignment</Label>
              <Select value={imageAlign} onValueChange={setImageAlign}>
                <SelectTrigger>
                  <SelectValue placeholder="Select alignment" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleInsert} disabled={!selectedImage}>
            Insert Image
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
